import React, { memo } from "react";
import { TEXT_BASE } from "../../constants/typography";

const STATUS_THEMES = {
  success: {
    statuses: [
      "delivered",
      "delivered shipments",
      "confirmed",
      "fulfilled",
      "paid",
      "extra charge paid",
      "success",
      "approved",
      "received",
      "verified",
      "self pickup",
      "walkin",
    ],
    textClass: "text-state-success-text",
    bgClass: "bg-state-success-bg",
    borderClass: "border-state-success-border",
    dotClass: "bg-state-success-dot",
    dotTextClass: "text-state-success-dot",
  },

  danger: {
    statuses: [
      "failed",
      "cancelled",
      "dispute lost",
      "false",
      "shipment canceled",
      "unpaid",
      "unpaid fulfillment",
      "return",
      "unverified",
    ],
    textClass: "text-state-danger-text",
    bgClass: "bg-state-danger-bg",
    borderClass: "border-state-danger-border",
    dotClass: "bg-state-danger-dot",
    dotTextClass: "text-state-danger-dot",
  },

  warning: {
    statuses: [
      "attention",
      "attention / partially fulfilled",
      "partially fulfilled",
      "disputed",
      "notreceived",
      "dispute",
      "attended",
      "self delivery",
      "in transit",
      "on hold",
    ],
    textClass: "text-state-warning-text",
    bgClass: "bg-state-warning-bg",
    borderClass: "border-state-warning-border",
    dotClass: "bg-state-warning-dot",
    dotTextClass: "text-state-warning-dot",
  },

  info: {
    statuses: ["processing", "marketplace", "packing"],
    textClass: "text-state-info-text",
    bgClass: "bg-state-info-bg",
    borderClass: "border-state-info-border",
    dotClass: "bg-state-info-dot",
    dotTextClass: "text-state-info-dot",
  },

  neutral: {
    statuses: [
      "archived",
      "creation",
      "pending / new",
      "total fulfillment",
      "pending",
    ],
    textClass: "text-state-neutral-text",
    bgClass: "bg-state-neutral-bg",
    borderClass: "border-state-neutral-border",
    dotClass: "bg-state-neutral-dot",
    dotTextClass: "text-state-neutral-dot",
  },

  purple: {
    statuses: ["manual", "label created"],
    textClass: "text-state-purple-text",
    bgClass: "bg-state-purple-bg",
    borderClass: "border-state-purple-border",
    dotClass: "bg-state-purple-dot",
    dotTextClass: "text-state-purple-dot",
  },

  sky: {
    statuses: ["import"],
    textClass: "text-state-sky-text",
    bgClass: "bg-state-sky-bg",
    borderClass: "border-state-sky-border",
    dotClass: "bg-state-sky-dot",
    dotTextClass: "text-state-sky-dot",
  },
};

const STATUS_MAP = Object.entries(STATUS_THEMES).reduce(
  (map, [themeKey, { statuses }]) => {
    statuses.forEach((status) => {
      map[status.toLowerCase()] = themeKey;
    });

    return map;
  },
  {},
);

const DEFAULT_THEME = STATUS_THEMES.info;

const formatLabel = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

function StatusIcons({
  status,
  countBadge,
  isPrint = false,
  showTitle = false,
  noBg = false,
  noDot = false,
  noBorder = true,
}) {
  if (typeof status !== "string" || !status.trim()) {
    return null;
  }

  const normalizedStatus = status.trim().toLowerCase();

  const theme = STATUS_THEMES[STATUS_MAP[normalizedStatus]] || DEFAULT_THEME;

  const label = formatLabel(status);

  const bgClass = noBg ? "bg-transparent" : theme.bgClass;

  const borderClass =
    noBg || noBorder
      ? "border-transparent border-0"
      : `border ${theme.borderClass}`;

  // html2canvas has repeatedly mishandled this badge in print output: flex-1
  // collapsed to zero width and dropped the text; inline vertical-align:
  // middle (font-baseline-dependent) left text and dot misaligned; a
  // table-cell layout then rendered doubled/ghosted. Every fix chased a
  // different layout-engine quirk. Print now uses the flattest markup
  // possible — one span, the dot as a literal inline bullet character
  // colored via text- (not a separate positioned box) — so there's no
  // layout construct left for it to get wrong.
  if (isPrint) {
    return (
      <span
        title={showTitle ? label : undefined}
        className={`
          rounded
          px-[5px]
          py-[3px]
          ${TEXT_BASE}
          font-semibold
          ${bgClass}
          ${borderClass}
          ${theme.textClass}
        `}
        style={{ whiteSpace: "normal", wordBreak: "break-word" }}
      >
        {!noDot && (
          <span
            className={theme.dotTextClass}
            style={{ position: "relative", top: "1px" }}
          >
            {"● "}
          </span>
        )}
        {label}
        {countBadge !== undefined && countBadge !== null && ` ${countBadge}`}
      </span>
    );
  }

  return (
    <div
      title={showTitle ? label : undefined}
      className={`
        inline-flex
        max-w-full
        min-w-0
        items-center
        justify-start
        rounded
        px-[5px]
        py-[3px]
        ${TEXT_BASE}
        font-semibold
        ${bgClass}
        ${borderClass}
        ${theme.textClass}
      `}
    >
      {!noDot && (
        <span
          className={`mr-1.5 aspect-square h-1.5 w-1.5 shrink-0 self-center rounded-full ${theme.dotClass}`}
        />
      )}

      <span className="min-w-0 max-w-full flex-1 truncate">
        {label}
        {countBadge !== undefined && countBadge !== null && (
          <span> {countBadge}</span>
        )}
      </span>
    </div>
  );
}

export default memo(StatusIcons);
